import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import '../styles/pages/TermsPage.css';

const TermsPage = () => {
  return (
    <>
      <Helmet>
        <title>Termos de Uso — LifeUp</title>
        <meta
          name="description"
          content="Termos de Uso do LifeUp. Condições de uso da plataforma, assinaturas, conduta, propriedade intelectual, isenção médica e contato."
        />
        <link rel="canonical" href="https://lifyup.com.br/termos" />
      </Helmet>

      <div className="legal-page">
        <Header />

        <main className="legal-content">
          <div className="container">
            <header className="legal-header">
              <h1 className="legal-title">Termos de Uso</h1>
              <p className="legal-updated">Versão 2.0 — vigente a partir de 1º de junho de 2026</p>
              <p className="legal-updated">Última atualização: 1º de junho de 2026</p>
            </header>

            <nav className="legal-toc" aria-label="Sumário">
              <h3>Sumário</h3>
              <ol>
                <li><a href="#aceitacao">Aceitação dos Termos</a></li>
                <li><a href="#servico">Descrição do Serviço</a></li>
                <li><a href="#elegibilidade">Elegibilidade e Idade Mínima</a></li>
                <li><a href="#conta">Conta de Usuário</a></li>
                <li><a href="#assinaturas">Planos, Assinaturas e Pagamentos</a></li>
                <li><a href="#renovacao">Renovação Automática e Cancelamento</a></li>
                <li><a href="#reembolso">Política de Reembolso</a></li>
                <li><a href="#conduta">Conduta do Usuário</a></li>
                <li><a href="#conteudo">Conteúdo Gerado por Usuário</a></li>
                <li><a href="#pi">Propriedade Intelectual</a></li>
                <li><a href="#disclaimer">Isenção Médica e Emergências</a></li>
                <li><a href="#responsabilidade">Limitação de Responsabilidade</a></li>
                <li><a href="#cancelamento">Cancelamento, Suspensão e Exclusão</a></li>
                <li><a href="#dmca">Direitos Autorais e Notificações DMCA</a></li>
                <li><a href="#apple-google">App Store, Google Play e Lojas de Aplicativos</a></li>
                <li><a href="#alteracoes">Alterações nos Termos</a></li>
                <li><a href="#lei">Lei Aplicável e Foro</a></li>
                <li><a href="#contato">Contato</a></li>
              </ol>
            </nav>

            <section id="aceitacao" className="legal-section">
              <h2>1. Aceitação dos Termos</h2>
              <p>
                Estes Termos de Uso (&quot;Termos&quot;) regem o acesso e o uso do aplicativo móvel, do site
                {' '}<strong>lifyup.com.br</strong>, do dashboard administrativo e dos demais serviços oferecidos pela LifeUp
                (coletivamente, &quot;Plataforma&quot;). Ao criar uma conta, acessar ou utilizar a Plataforma, você declara que
                leu, entendeu e concorda integralmente com estes Termos e com a nossa{' '}
                <a href="/privacidade">Política de Privacidade</a>.
              </p>
              <p>
                Se você <strong>não concorda</strong> com qualquer disposição destes Termos, não utilize a Plataforma.
                O uso continuado após qualquer atualização caracteriza aceite tácito da versão mais recente.
              </p>
            </section>

            <section id="servico" className="legal-section">
              <h2>2. Descrição do Serviço</h2>
              <p>
                A LifeUp é uma plataforma digital de bem-estar comportamental organizada em programas temáticos
                (&quot;Programas&quot;), tais como <strong>Freedom</strong> (recuperação de comportamentos compulsivos relacionados
                a pornografia), <strong>Balance</strong> (equilíbrio emocional e saúde mental) e novos programas que poderão
                ser lançados no futuro.
              </p>
              <p>Cada Programa pode incluir, sem limitação, as seguintes funcionalidades:</p>
              <ul>
                <li>Rastreamento de progresso, streaks e estatísticas pessoais.</li>
                <li>Gamificação com badges, níveis, missões e desafios.</li>
                <li>Comunidade anônima de apoio entre pares.</li>
                <li>Chat em tempo real entre usuários e/ou voluntários.</li>
                <li>Diário emocional, registros de gatilhos e check-ins diários.</li>
                <li>Botão de emergência (&quot;panic button&quot;) e técnicas de coping.</li>
                <li>Conteúdo educativo (artigos, áudios, meditações, vídeos).</li>
                <li>Notificações motivacionais e lembretes configuráveis.</li>
              </ul>
              <p>
                A LifeUp pode adicionar, modificar ou descontinuar funcionalidades a qualquer tempo, observado o
                disposto na seção sobre <a href="#alteracoes">alterações destes Termos</a>.
              </p>
            </section>

            <section id="elegibilidade" className="legal-section">
              <h2>3. Elegibilidade e Idade Mínima</h2>
              <p>Para utilizar a Plataforma, você deve:</p>
              <ul>
                <li>Ter pelo menos <strong>18 anos completos</strong>;</li>
                <li>Ter plena capacidade civil para celebrar este contrato;</li>
                <li>Fornecer informações verdadeiras, completas e atualizadas;</li>
                <li>Manter suas credenciais de acesso em sigilo absoluto.</li>
              </ul>
              <p>
                A Plataforma não é destinada a menores de 18 anos. Caso identifiquemos cadastro de menor, a conta
                será encerrada e os dados removidos conforme nossa{' '}
                <a href="/privacidade">Política de Privacidade</a>.
              </p>
            </section>

            <section id="conta" className="legal-section">
              <h2>4. Conta de Usuário</h2>
              <h3>4.1 Criação e segurança</h3>
              <ul>
                <li>Você pode usar pseudônimo (nickname) para preservar sua privacidade.</li>
                <li>É obrigatório fornecer um e-mail válido para recuperação de acesso.</li>
                <li>Recomenda-se senha forte (≥ 12 caracteres, com letras, números e símbolos).</li>
                <li>Você é responsável por todas as atividades realizadas em sua conta.</li>
                <li>
                  Notifique-nos imediatamente em <a href="mailto:suporte@lifyup.com.br">suporte@lifyup.com.br</a>
                  {' '}em caso de suspeita de acesso não autorizado.
                </li>
              </ul>
              <h3>4.2 Verificação e duplicidade</h3>
              <p>
                Reservamo-nos o direito de exigir verificação adicional (e-mail, telefone) e de remover contas
                duplicadas, falsas ou criadas em violação a estes Termos.
              </p>
            </section>

            <section id="assinaturas" className="legal-section">
              <h2>5. Planos, Assinaturas e Pagamentos</h2>
              <p>
                Parte das funcionalidades da Plataforma é gratuita (&quot;Plano Free&quot;). Funcionalidades adicionais
                (&quot;Plano Premium&quot; ou equivalente) exigem assinatura paga, com cobrança mensal ou anual conforme o
                plano contratado.
              </p>
              <h3>5.1 Preços</h3>
              <ul>
                <li>Os preços vigentes são exibidos nas telas de assinatura dentro do app e em /assinar.</li>
                <li>Valores podem variar por região, moeda local e promoções vigentes.</li>
                <li>Impostos incidentes serão destacados quando aplicável.</li>
              </ul>
              <h3>5.2 Formas de pagamento</h3>
              <ul>
                <li>
                  <strong>Aplicativo móvel (iOS):</strong> processado exclusivamente pela{' '}
                  <strong>Apple App Store</strong>, conforme regras da Apple Inc.
                </li>
                <li>
                  <strong>Aplicativo móvel (Android):</strong> processado exclusivamente pelo{' '}
                  <strong>Google Play Billing</strong>, conforme regras do Google LLC.
                </li>
                <li>
                  <strong>Web (lifyup.com.br):</strong> processado por gateways como Stripe e/ou Mercado Pago,
                  aceitando cartões de crédito, débito, PIX e boleto (quando disponíveis).
                </li>
              </ul>
              <div className="info-box">
                <p>
                  <strong>Importante:</strong> assinaturas iniciadas via App Store ou Google Play são governadas
                  pelos contratos das respectivas lojas. Para cancelamento, reembolso ou alteração de método de
                  pagamento, você deve utilizar diretamente as ferramentas da loja, conforme detalhado a seguir.
                </p>
              </div>
            </section>

            <section id="renovacao" className="legal-section">
              <h2>6. Renovação Automática e Cancelamento</h2>
              <p>
                Todas as assinaturas pagas são contratadas com <strong>renovação automática</strong>: ao final do
                período vigente (mês ou ano), seu método de pagamento será cobrado novamente para o próximo ciclo,
                até que você cancele.
              </p>
              <h3>6.1 Como cancelar — iOS (App Store)</h3>
              <ol>
                <li>Abra <strong>Ajustes</strong> &gt; toque em seu nome &gt; <strong>Assinaturas</strong>.</li>
                <li>Selecione <strong>LifeUp</strong>.</li>
                <li>Toque em <strong>Cancelar Assinatura</strong> e confirme.</li>
              </ol>
              <h3>6.2 Como cancelar — Android (Google Play)</h3>
              <ol>
                <li>Abra o app <strong>Google Play</strong>.</li>
                <li>Toque na sua foto &gt; <strong>Pagamentos e assinaturas</strong> &gt; <strong>Assinaturas</strong>.</li>
                <li>Selecione <strong>LifeUp</strong> &gt; <strong>Cancelar assinatura</strong>.</li>
              </ol>
              <h3>6.3 Como cancelar — Web</h3>
              <ol>
                <li>Acesse <strong>lifyup.com.br</strong> e faça login.</li>
                <li>Vá em <strong>Configurações &gt; Assinatura</strong>.</li>
                <li>Clique em <strong>Cancelar plano</strong> e confirme.</li>
              </ol>
              <p>
                O cancelamento interrompe a renovação para o ciclo seguinte. O acesso premium permanece ativo até
                o fim do período já pago. Não há cobrança proporcional para o período em uso.
              </p>
            </section>

            <section id="reembolso" className="legal-section">
              <h2>7. Política de Reembolso</h2>

              <h3>7.1 Direito de arrependimento (Brasil — CDC art. 49)</h3>
              <p>
                Consumidores residentes no Brasil que contratarem assinatura paga <strong>fora do estabelecimento
                comercial</strong> (incluindo pela internet) têm direito a desistir do contrato no prazo de{' '}
                <strong>7 (sete) dias corridos</strong> contados da contratação, com reembolso integral do valor pago.
                A solicitação deve ser feita ao canal de pagamento utilizado:
              </p>
              <ul>
                <li>
                  <strong>Web (Stripe/Mercado Pago):</strong> envie e-mail para{' '}
                  <a href="mailto:suporte@lifyup.com.br">suporte@lifyup.com.br</a> com o assunto &quot;Solicitação
                  de Reembolso — 7 dias&quot;, informando o e-mail da conta e a data da compra.
                </li>
                <li>
                  <strong>App Store:</strong> solicite reembolso em{' '}
                  <a href="https://reportaproblem.apple.com" target="_blank" rel="noopener noreferrer">
                    reportaproblem.apple.com
                  </a>{' '}— a decisão final é da Apple.
                </li>
                <li>
                  <strong>Google Play:</strong> solicite em{' '}
                  <a href="https://support.google.com/googleplay" target="_blank" rel="noopener noreferrer">
                    support.google.com/googleplay
                  </a>{' '}— a decisão final é do Google.
                </li>
              </ul>

              <h3>7.2 Fora do prazo de arrependimento</h3>
              <p>
                Após os 7 dias, reembolsos são avaliados caso a caso, salvo disposição legal em contrário no seu
                país de residência. Cobranças realizadas por App Store ou Google Play seguem exclusivamente as
                políticas destas lojas — a LifeUp não tem poder de reverter cobranças realizadas por terceiros.
              </p>
            </section>

            <section id="conduta" className="legal-section">
              <h2>8. Conduta do Usuário</h2>
              <h3>8.1 Comportamento esperado</h3>
              <ul>
                <li>Uso pessoal e não comercial da Plataforma.</li>
                <li>Respeito, empatia e acolhimento com outros membros.</li>
                <li>Compartilhamento construtivo de experiências de recuperação e bem-estar.</li>
                <li>Apoio mútuo, encorajamento e linguagem inclusiva.</li>
              </ul>
              <h3>8.2 Comportamento proibido</h3>
              <p>É expressamente vedado:</p>
              <ul>
                <li>Assediar, intimidar, ameaçar ou discriminar outros usuários por qualquer motivo;</li>
                <li>Compartilhar conteúdo pornográfico, sexualmente explícito, violento, gore ou ilegal;</li>
                <li>Compartilhar conteúdo que incentive automutilação, suicídio ou recaída;</li>
                <li>Realizar spam, propaganda, recrutamento religioso/político não autorizado;</li>
                <li>Violar direitos autorais, marcas ou propriedade intelectual de terceiros;</li>
                <li>Praticar qualquer ato ilegal, incluindo tráfico, fraude ou exploração de vulneráveis;</li>
                <li>Tentar hackear, escanear, sobrecarregar ou comprometer a segurança da Plataforma;</li>
                <li>Coletar, raspar ou extrair dados de outros usuários por qualquer meio automatizado;</li>
                <li>Criar múltiplas contas para manipular rankings, conquistas ou enganar a comunidade;</li>
                <li>Publicar dados pessoais de terceiros sem consentimento expresso (doxxing).</li>
              </ul>
              <p>
                Violações podem resultar em advertência, remoção de conteúdo, suspensão temporária ou
                <strong> banimento permanente</strong> da conta, sem direito a reembolso e sem prejuízo das medidas
                cíveis e criminais cabíveis.
              </p>
            </section>

            <section id="conteudo" className="legal-section">
              <h2>9. Conteúdo Gerado por Usuário</h2>
              <h3>9.1 Propriedade</h3>
              <p>
                Você mantém todos os direitos autorais sobre o conteúdo que publica (posts, diário, mensagens,
                fotos, áudios). Ao publicar conteúdo na Plataforma, você concede à LifeUp uma licença mundial,
                não exclusiva, gratuita, sublicenciável e transferível para hospedar, armazenar, reproduzir,
                modificar (somente para fins técnicos de exibição e moderação), distribuir e exibir tal conteúdo
                dentro da Plataforma, com o propósito exclusivo de prestar e melhorar os serviços.
              </p>
              <h3>9.2 Responsabilidade</h3>
              <p>
                Você é o único responsável pelo conteúdo que publica e declara possuir todos os direitos
                necessários para tal. A LifeUp não endossa nem garante a precisão de conteúdo gerado por
                usuários.
              </p>
              <h3>9.3 Moderação</h3>
              <p>
                Utilizamos moderação humana e/ou automatizada para identificar e remover conteúdo que viole estes
                Termos, leis aplicáveis ou direitos de terceiros. A remoção pode ocorrer sem aviso prévio.
              </p>
            </section>

            <section id="pi" className="legal-section">
              <h2>10. Propriedade Intelectual</h2>
              <p>
                Todo o conteúdo proprietário da Plataforma — incluindo design, código-fonte, ilustrações, ícones,
                logotipos, marcas, textos editoriais, conteúdo dos Programas, áudios de meditação, identidade
                visual e demais elementos — é de titularidade exclusiva da LifeUp ou de seus licenciadores e está
                protegido pelas leis de propriedade intelectual aplicáveis.
              </p>
              <p>É expressamente vedado:</p>
              <ul>
                <li>Copiar, modificar, traduzir, distribuir, vender ou explorar comercialmente o conteúdo;</li>
                <li>Fazer engenharia reversa, descompilar, desmontar ou criar obras derivadas;</li>
                <li>Utilizar marcas, logotipos ou identidade visual da LifeUp sem autorização escrita prévia.</li>
              </ul>
            </section>

            <section id="disclaimer" className="legal-section">
              <h2>11. Isenção Médica e Emergências</h2>
              <div className="warning-box">
                <p>
                  <strong>AVISO IMPORTANTE — LEIA COM ATENÇÃO.</strong>
                </p>
                <p>
                  A LifeUp é uma <strong>ferramenta de suporte ao bem-estar</strong> e NÃO substitui avaliação,
                  diagnóstico ou tratamento médico, psicológico, psiquiátrico ou de qualquer profissional de saúde
                  habilitado.
                </p>
                <ul>
                  <li>Não fornecemos diagnósticos clínicos.</li>
                  <li>Não prescrevemos medicamentos ou tratamentos.</li>
                  <li>Não substituímos terapia profissional, grupos de apoio presenciais (AA/NA), médico ou psicólogo.</li>
                  <li>Em casos de sofrimento intenso, ideação suicida ou crise grave, procure ajuda profissional imediata.</li>
                </ul>
                <p>
                  <strong>Em emergências no Brasil:</strong> ligue para o <strong>CVV — Centro de Valorização da
                  Vida</strong> no <strong>188</strong> (24h, gratuito) ou para o <strong>SAMU 192</strong>.
                  Em outros países, contate o serviço de emergência local.
                </p>
              </div>
            </section>

            <section id="responsabilidade" className="legal-section">
              <h2>12. Limitação de Responsabilidade</h2>
              <p>
                Na máxima extensão permitida pela legislação aplicável, a Plataforma é fornecida{' '}
                <strong>&quot;COMO ESTÁ&quot;</strong> e <strong>&quot;CONFORME DISPONÍVEL&quot;</strong>, sem garantias de qualquer
                espécie, expressas ou implícitas. A LifeUp não se responsabiliza por:
              </p>
              <ul>
                <li>Resultados específicos de recuperação — cada jornada é individual e depende de múltiplos fatores;</li>
                <li>Decisões tomadas pelo usuário com base em conteúdo da Plataforma;</li>
                <li>Conteúdo, conduta ou consequências de interações com outros usuários;</li>
                <li>Interrupções temporárias, falhas técnicas, perda de dados, indisponibilidade de servidores;</li>
                <li>Danos indiretos, lucros cessantes, perda de oportunidade ou dano moral;</li>
                <li>Ações ou omissões de terceiros (provedores de pagamento, lojas de aplicativos, redes sociais).</li>
              </ul>
              <p>
                Esta limitação não exclui responsabilidades que não possam ser limitadas ou excluídas pela
                legislação aplicável, incluindo direitos do consumidor previstos no Código de Defesa do
                Consumidor brasileiro.
              </p>
            </section>

            <section id="cancelamento" className="legal-section">
              <h2>13. Cancelamento, Suspensão e Exclusão</h2>
              <h3>13.1 Por iniciativa do usuário</h3>
              <p>Você pode, a qualquer momento:</p>
              <ul>
                <li>
                  <strong>Cancelar a assinatura paga</strong> conforme procedimentos da{' '}
                  <a href="#renovacao">seção 6</a>;
                </li>
                <li>
                  <strong>Excluir sua conta</strong> diretamente no app (Configurações &gt; Conta &gt; Excluir
                  conta), pelo site (lifyup.com.br/excluir-conta) ou por e-mail
                  (<a href="mailto:dpo@lifyup.com.br">dpo@lifyup.com.br</a>).
                </li>
              </ul>
              <p>
                A exclusão é definitiva e remove dados conforme prazos descritos na nossa{' '}
                <a href="/privacidade">Política de Privacidade</a>. Determinados dados (fiscais, fraudes, registros
                obrigatórios) podem ser retidos por exigência legal.
              </p>

              <h3>13.2 Por iniciativa da LifeUp</h3>
              <p>
                Podemos suspender ou encerrar sua conta, com ou sem aviso prévio, em caso de violação destes
                Termos, fraude, uso abusivo, requisição legal ou cessação total do serviço. Em casos de
                banimento por violação grave, não há direito a reembolso de períodos não usufruídos.
              </p>
            </section>

            <section id="dmca" className="legal-section">
              <h2>14. Direitos Autorais e Notificações DMCA</h2>
              <p>
                Respeitamos os direitos de propriedade intelectual e atendemos notificações conforme o{' '}
                <strong>Digital Millennium Copyright Act (DMCA)</strong> e a legislação brasileira de direitos
                autorais (Lei 9.610/98). Caso entenda que conteúdo publicado na Plataforma viola seus direitos,
                envie notificação para <a href="mailto:legal@lifyup.com.br">legal@lifyup.com.br</a> contendo:
              </p>
              <ol>
                <li>Identificação detalhada da obra protegida;</li>
                <li>Identificação do conteúdo infrator (URL ou caminho dentro do app);</li>
                <li>Seus dados completos (nome, endereço, telefone, e-mail);</li>
                <li>Declaração de boa-fé de que o uso não é autorizado;</li>
                <li>Declaração, sob penas da lei, de veracidade das informações e de titularidade dos direitos;</li>
                <li>Assinatura física ou eletrônica.</li>
              </ol>
              <p>
                Removeremos prontamente o conteúdo notificado e poderemos suspender contas reincidentes.
              </p>
            </section>

            <section id="apple-google" className="legal-section">
              <h2>15. App Store, Google Play e Lojas de Aplicativos</h2>
              <p>
                O aplicativo móvel da LifeUp é distribuído pela <strong>Apple App Store</strong> e pelo{' '}
                <strong>Google Play</strong>. As seguintes disposições adicionais se aplicam:
              </p>
              <h3>15.1 Apple App Store</h3>
              <ul>
                <li>
                  Estes Termos são celebrados entre você e a LifeUp; a Apple Inc. não é parte deste contrato e
                  <strong> não é responsável</strong> pelo app ou seu conteúdo.
                </li>
                <li>
                  Você reconhece que estes Termos estão sujeitos ao{' '}
                  <a
                    href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    EULA padrão da Apple
                  </a>
                  . Em caso de conflito, prevalecem as regras da Apple naquilo que forem aplicáveis.
                </li>
                <li>
                  Reclamações de produto, garantia legal e questões de propriedade intelectual relativas ao
                  app devem ser direcionadas à LifeUp, não à Apple.
                </li>
              </ul>
              <h3>15.2 Google Play</h3>
              <ul>
                <li>
                  O Google LLC não é parte deste contrato e não tem responsabilidade pelo conteúdo do app.
                </li>
                <li>
                  Cobranças e reembolsos via Google Play seguem os{' '}
                  <a
                    href="https://play.google.com/intl/pt-BR_br/about/play-terms/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Termos de Serviço do Google Play
                  </a>
                  .
                </li>
                <li>
                  Você poderá excluir sua conta diretamente do aplicativo ou pelo site, conforme exigência do
                  Google Play (User Data Deletion Requirement).
                </li>
              </ul>
            </section>

            <section id="alteracoes" className="legal-section">
              <h2>16. Alterações nos Termos</h2>
              <p>
                Podemos atualizar estes Termos periodicamente para refletir evoluções legais, regulatórias ou de
                produto. Quando a mudança for <strong>relevante ou prejudicial ao usuário</strong>, comunicaremos
                com <strong>antecedência mínima de 30 dias</strong> via e-mail e/ou aviso destacado no app/site.
              </p>
              <p>
                O uso continuado da Plataforma após a entrada em vigor da nova versão caracteriza concordância
                com os Termos atualizados. Se você não concordar, deve cessar o uso e encerrar sua conta.
              </p>
            </section>

            <section id="lei" className="legal-section">
              <h2>17. Lei Aplicável e Foro</h2>
              <p>
                Estes Termos são regidos pelas leis da <strong>República Federativa do Brasil</strong>. Fica eleito
                o foro da comarca de <strong>São Paulo/SP</strong>, com renúncia expressa a qualquer outro, por
                mais privilegiado que seja, para dirimir quaisquer controvérsias decorrentes destes Termos,
                ressalvado o direito do consumidor de ajuizar ação em seu próprio domicílio, nos termos do
                Código de Defesa do Consumidor.
              </p>
            </section>

            <section id="contato" className="legal-section">
              <h2>18. Contato</h2>
              <p>Para dúvidas, solicitações ou notificações relacionadas a estes Termos:</p>
              <ul>
                <li><strong>Suporte geral:</strong> <a href="mailto:suporte@lifyup.com.br">suporte@lifyup.com.br</a></li>
                <li><strong>Jurídico e DMCA:</strong> <a href="mailto:legal@lifyup.com.br">legal@lifyup.com.br</a></li>
                <li><strong>Privacidade / DPO:</strong> <a href="mailto:dpo@lifyup.com.br">dpo@lifyup.com.br</a></li>
                <li><strong>Site:</strong> <a href="https://lifyup.com.br" target="_blank" rel="noopener noreferrer">lifyup.com.br</a></li>
              </ul>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default TermsPage;
